import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { authedV3Request } from "utils/api/requests";
import { selectAuthToken } from "containers/App/selectors";

/*
 * Account-level mutations that belong to the authed shell rather than to any
 * one page — App owns the session, and these act on the subscription the
 * session describes.
 */

/*
 * `POST /api/v3/billing_portal_session` — verified against the live
 * implementation: `Api::V3::BillingPortalSessionsController#create` (rails
 * #1224), deployed to gifthing (an unauthenticated POST returns 401, not 404).
 * It reads the Stripe customer from `current_user.stripe_customer_id` (never
 * from params), and responds `{ success: true, url }` — or `404
 * { success: false }` when the account has no Stripe customer behind it (a
 * legacy sub billed outside Stripe), which surfaces as an error toast and
 * leaves the customer where they were rather than as a crash. The path is a
 * named constant so a future spelling change is one edit.
 *
 * An optional `flow` in the request body (e.g. `subscription_update`) asks
 * rails to open the portal directly on a specific Stripe flow instead of its
 * default landing page. Today's deployed controller takes no params at all,
 * so it silently ignores an unrecognized body and returns the same plain
 * portal URL either way — sending `flow` is therefore safe against both the
 * current backend and the one that adds support for it.
 */
export const BILLING_PORTAL_SESSION_URI = "v3/billing_portal_session";

/*
 * Open a Stripe Customer Portal session for the signed-in user.
 *
 * The portal — not a form of ours — is where a customer adds or updates a card,
 * cancels (at period end), and reads their invoice history. Nothing about a
 * payment method touches this client: rails mints a short-lived session against
 * the user's own Stripe customer and returns its `url`, and the browser simply
 * goes there. That is the whole reason this is a redirect rather than an
 * embedded form — it keeps the dashboard out of PCI scope entirely, with no
 * payments SDK in the bundle.
 *
 * A mutation rather than a query, and the distinction is not cosmetic: a portal
 * session is single-use and short-lived, so it must be minted when the customer
 * asks for it and never speculatively fetched, cached, or refetched in the
 * background.
 *
 * `mutate`/`mutateAsync` take an optional `{ flow }` variables object — see
 * `containers/UpgradeCta`, which mints a `subscription_update` flow session
 * and falls back to a plain one when the flow request itself fails.
 */
export const useBillingPortalSession = (options = {}) => {
  const authToken = useSelector(selectAuthToken());

  return useMutation({
    mutationFn: (variables = {}) =>
      authedV3Request(BILLING_PORTAL_SESSION_URI, authToken, {
        method: "POST",
        body: JSON.stringify(variables),
      }),
    ...options,
  });
};
